import {
  DeleteLaptopRequest,
  EnrollLaptopRequest,
  EnrollLaptopResponse,
  RawLaptopData,
  RegisterLaptopRequest,
  UpdateLaptopRoleRequest
} from './types'
import { managementApi } from '../../modules/servers/api'
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit'

const tagType = 'laptops' as const

export const laptopsApi = managementApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all laptops for organisation
    getAllOrganisationLaptops: builder.query<
      EntityState<RawLaptopData, RawLaptopData['id']>,
      string
    >({
      extraOptions: { isListWithEmpty404Response: true },
      query: (organisationId) => `organisations/${organisationId}/laptops`,
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: tagType, id })),
              { type: tagType, id: 'LIST' }
            ]
          : [{ type: tagType, id: 'LIST' }],
      transformResponse: (data: RawLaptopData[]) => {
        const entityAdapter = createEntityAdapter<RawLaptopData>()
        return entityAdapter.addMany(entityAdapter.getInitialState(), data)
      }
    }),

    updateLaptopRole: builder.mutation<RawLaptopData, UpdateLaptopRoleRequest>({
      query: (body) => ({
        url: `laptop/role`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (result) => {
        return [
          { type: tagType, id: 'LIST' },
          { type: tagType, id: result?.id || '' }
        ]
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        // TODO: Optimistic update can be handled here if needed
      }
    }),

    deleteLaptopFromOrg: builder.mutation<void, DeleteLaptopRequest>({
      query: (body) => ({
        url: `laptop/remove`,
        method: 'DELETE',
        body
      }),
      invalidatesTags: (result, error, arg) => {
        return [
          { type: tagType, id: 'LIST' },
          { type: tagType, id: arg.id }
        ]
      },
      onQueryStarted: async (req, { dispatch, queryFulfilled }) => {
        // TODO: Optimistic update can be handled here if needed
      }
    }),

    registerLaptopWithOrganisation: builder.mutation<
      void,
      RegisterLaptopRequest
    >({
      query: (body) => ({
        url: `laptop/organisation`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: tagType, id: 'LIST' }]
    }),

    enrollLaptop: builder.mutation<EnrollLaptopResponse, EnrollLaptopRequest>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
          state: params.state,
          code_challenge: params.code_challenge,
          code_challenge_method: params.code_challenge_method,
          organisation: params.organisation
        })
        return {
          url: `laptop/enrollment?${searchParams.toString()}`,
          method: 'POST'
        }
      },
      invalidatesTags: [{ type: tagType, id: 'LIST' }]
    })
  })
})

export const {
  useGetAllOrganisationLaptopsQuery,
  useUpdateLaptopRoleMutation,
  useDeleteLaptopFromOrgMutation,
  useRegisterLaptopWithOrganisationMutation,
  useEnrollLaptopMutation
} = laptopsApi
